/// <reference path="../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import { CreateAuthenticationProvider } from '../backend/factory/AuthenticationProviderFactory'
import { IsAuthError, IsNoUserSignedInError } from '../backend/office-js/auth/AuthErrors';
import { TranscriptionRoutePathNames } from './routes';
import { IAuthenticationProvider, GetAccessTokenOptions } from '../backend/interfaces/IAuthenticationProvider';
import * as Logger from '../backend/TelemetryLogger/Details/Logger';
import { StartInitializingRuntimeManager } from '../backend/augloop/Augloop';
import { GetAugloopResourceUrl } from '../backend/augloop/AugloopConfig';
import { AugloopError, AugloopErrors } from '../backend/augloop/AugloopErrors';
import { MAX_BOOT_TIME_MILLISECONDS, IS_DEV_BUILD, DEV_AUGLOOP_URL } from '../AppConstants';
import { SetFailureStateAndRouteToFailure } from './actions/FailureActions';
import { InitializeAudioBackup, AudioBackupAndRecoveryStatus } from './common/actions/RecoveringActions';
import { ErrorStringFromException, IsUnlimitedFileUploadMinutesAvailableForUser } from '../utils/utils';
import { NetworkError, NetworkErrors } from '../utils/Errors';
import * as LoggerFactory from '../backend/TelemetryLogger/Details/LoggerFactory';
import { LogEvents } from '../utils/LogEvents';
import { BootKeys } from './common/telemetry/BootKeys';
import { LogError, ShouldLogErrorAsWarning } from '../utils/ErrorTelemetry';
import { RenderToPaneKeys } from './common/telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../backend/interfaces/ILogEvent';
import { LogEventNames } from '../backend/interfaces/ITelemetryLogger';
import { CheckNetworkConnection, IsConnectedToNetwork } from './NetworkConnectionMonitor';
import { RegisterServiceWorker } from '../utils/ServiceWorkerUtil';
import * as GlobalErrorMonitor from '../utils/GlobalErrorMonitor';
import { ResettableTimeout } from '../utils/ResettableTimeout';
import { initializeIcons } from 'office-ui-fabric-react';
import { HostAppSettings, HostAppSettingName, HostAppName, getAuthEnvironment } from '../backend/office-js/HostAppSettings';
import { LoadTranscriptionStringsAsync, LoadTranscriptionStringsError, LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE } from './LoadTranscriptionStrings';
import { BootError, BootErrors } from './BootErrors';
import { NavigateToLandingPageAfterAudioBackupCheck } from './actions/LandingActions';
import { AudioBackupError, AudioBackupErrors } from '../backend/audioBackup/AudioBackupErrors';
import { TranscriptionTelemetryErrorCodes } from '../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../backend/interfaces/ITranscriptionErrorEvent';
import { RecordingEventManager } from '../backend/manager/RecordingEventManager/RecordingEventManager';
import { TranscriptionNotifyEvent } from '../backend/interfaces/IEventListener';
import { InitializeHostAppNotifer } from '../backend/factory/HostAppEventNotifierProviderFactory';
import { InitializeOneNoteTranscriptionManager, IsOneNoteNotebookIdentityEmpty } from '../backend/OneNote/OneNoteTranscriptionManager';
import { HostAppUtil } from '../backend/office-js/HostAppUtil';
import { FailureContinuation } from './common/enums/failureEnums';
import { BroadcastService } from '../backend/manager/eventManager/BroadcastService';
import { enableCopilotLicenseCheck, enableMsal5, isUseCustomIconServer } from '../utils/ChangeGate';
import { CopilotLicensingTask } from '../backend/copilot/CopilotLicensingTask';
import { getAuthEnvironmentFromAugLoopServiceUrl, getIconBaseUrlForEnvironment } from '../utils/AuthEnvironmentUtil';
import { AuthEnvironment } from '../backend/interfaces/IAuthenticationProvider';

function GetFailureReasonForInitializeAppError(error: any) : ITranscriptionErrorEvent {
  // If we couldn't even load strings, show an English string.
  if (error instanceof LoadTranscriptionStringsError || (error instanceof BootError && error.name == BootErrors.InvalidLoadTranscriptionStringsPromise))
    return {errorEventMessage : LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE, errorEventName: TranscriptionTelemetryErrorCodes.UnknownInitFailureReason};

  if (error instanceof BootError && error.name == BootErrors.IndexeddbFailedToOpen
    || error instanceof AudioBackupError && (error.name == AudioBackupErrors.UnsupportedBrowser || error.name == AudioBackupErrors.IndexedDBOpenError)) {
    return {errorEventMessage : TranscriptionStrings.CookieSettingsFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.CookieSettingsFailureReason};
  }

  if (error instanceof BootError && error.name == BootErrors.MaxBootTimeReached)
    return {errorEventMessage : TranscriptionStrings.InitHangFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.InitHangFailureReason};

  if (error instanceof AugloopError && error.name == AugloopErrors.SessionInitTimeout)
    return {errorEventMessage : TranscriptionStrings.UnableToConnectToTranscriptionServiceFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.UnableToConnectToTranscriptionserviceFailureReason};

  if (IsNoUserSignedInError(error)) {
    return {errorEventMessage : TranscriptionStrings.PrivateBrowsingNotSupported, errorEventName: TranscriptionTelemetryErrorCodes.PrivateBrowsingAuthFailure};
  }

  if (IsAuthError(error)) {
    return {errorEventMessage : TranscriptionStrings.UnableToAuthenticateNoBackupFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.UnableToAuthenticateNoBackupFailureReason};
  }

  if (error instanceof NetworkError && error.name == NetworkErrors.NoInternetConnection)
    return {errorEventMessage : TranscriptionStrings.ConnectionIssueFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason};

  return {errorEventMessage : TranscriptionStrings.UnknownInitFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.UnknownInitFailureReason};
}

//-----------------------------------------------------------
//-------------------  ASYNC TASKS --------------------------
function InitializeBackupStateTask(history: any): Promise<any> {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.InitializeBackupStateDuration);

  const initializeBackupStateTask = InitializeAudioBackup().then(
    async (backupAndState: AudioBackupAndRecoveryStatus) => {
      LogEvents.Boot.EndDurationMeasure(BootKeys.InitializeBackupStateDuration);
      return SetInitialPageFromBackupStateAndStoredTranscription(backupAndState, history);
    }
  );

  return initializeBackupStateTask;
}

let loadTranscriptionStringsTask: Promise<void>|null = null;
function LoadTranscriptionStringsTask(): Promise<any> {
  if (typeof TranscriptionStrings !== "undefined")
    return Promise.resolve();

  // Load transcription strings task is initialized outside of InitializeAsyncTasks because we need to await it if we fail anywhere during boot.
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.LoadTranscriptionStringsDuration);
  return LoadTranscriptionStringsAsync().then(() => {
    LogEvents.Boot.EndDurationMeasure(BootKeys.LoadTranscriptionStringsDuration);
  });
}

// Initialize any asynchronous tasks to be executed during boot.
// Returns a list of promises for such tasks.
function InitializeAsyncTasks(history: any, authProvider: IAuthenticationProvider): Promise<any>[] {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.InitializeAsyncTasksStageDuration);
  const asyncTasks: Promise<any>[] = [];

  loadTranscriptionStringsTask = LoadTranscriptionStringsTask();

  asyncTasks.push(loadTranscriptionStringsTask);
  asyncTasks.push(InitializeBackupStateTask(history));
  asyncTasks.push(CheckCopilotLicense(authProvider).then(() =>
    enableMsal5 ? PreCheckAuthToken(authProvider) : undefined
  ));

  LogEvents.Boot.EndDurationMeasure(BootKeys.InitializeAsyncTasksStageDuration);
  return asyncTasks;
}

// Pre-check auth token during boot to detect private browsing early.
// In private/incognito mode, silent auth fails with login_required and popup is not
// supported in MSAL5, so we surface a helpful message instead of waiting
// until file upload to discover the issue.
// Only rethrow for private-browsing errors; other auth failures (e.g. PackageNotLoaded
// in CI anonymous sessions) are non-fatal so the landing page can still render.
async function PreCheckAuthToken(authProvider: IAuthenticationProvider): Promise<void> {
  try {
    const resource = GetAugloopResourceUrl(authProvider.GetAuthEnvironment());
    const options: GetAccessTokenOptions = { resource };
    await authProvider.GetAccessToken(options);
  } catch (error) {
    if (IsNoUserSignedInError(error)) {
      throw error;
    }
    LogError(LogEventNames.Warning, "PreCheckAuthToken failed (non-fatal): " + ErrorStringFromException(error));
  }
}

// Wait for the asynchronous tasks executed during boot to finish
async function WaitForAsyncTasks(asyncTasks: Promise<any>[]) {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.WaitForAsyncTasksStageDuration);

  await Promise.all(asyncTasks);

  LogEvents.Boot.EndDurationMeasure(BootKeys.WaitForAsyncTasksStageDuration);
}

//------------------- END ASYNC TASKS -----------------------
//-----------------------------------------------------------

async function SetInitialPageFromBackupStateAndStoredTranscription(backupAndState: AudioBackupAndRecoveryStatus, history: any)
{
  if (history.location.pathname == TranscriptionRoutePathNames.Failure) {
    return;
  }

  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.SetInitialPageFromBackupStateAndStoredTranscriptionDuration);
  if (backupAndState.needsRecovery) {
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
      LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Recovery available, redired to landing"}]);
      LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
      history.replace(TranscriptionRoutePathNames.Loading);
      LogEvents.RenderToPane.EndDurationMeasure(RenderToPaneKeys.DurationSecs);
    } else {
      history.replace(TranscriptionRoutePathNames.Recovering, {audioBackup: backupAndState.audioBackup});
    }
  } else if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
    LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Pane loaded with preexisting transcription" }]);
    LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);

    if (backupAndState.audioBackup != null) {
      backupAndState.audioBackup.Close();
    }

    history.replace(TranscriptionRoutePathNames.Loading);
  } else {
    NavigateToLandingPageAfterAudioBackupCheck(backupAndState, history);
  }

  LogEvents.Boot.EndDurationMeasure(BootKeys.SetInitialPageFromBackupStateAndStoredTranscriptionDuration);
}

function StartInitializingAugLoopRuntimeManager(): string {
  let augLoopCloudALServiceUrl: string = IS_DEV_BUILD ? DEV_AUGLOOP_URL : HostAppSettings.GetSettingAsString(HostAppSettingName.AugLoopCloudALServiceUrl);
  if (augLoopCloudALServiceUrl == null || augLoopCloudALServiceUrl == '') {
    throw new Error('augLoopCloudALServiceUrl not defined');
  }

  StartInitializingRuntimeManager(augLoopCloudALServiceUrl);
  return augLoopCloudALServiceUrl;
}

// Execute any synchronous tasks required during boot.
function ExecuteSynchronousTasks(
  history: any,
  setAuthProvider: React.Dispatch<React.SetStateAction<IAuthenticationProvider | null>>,
  setIsAppLoaded: (isAppLoaded: boolean) => void,
  augloopServiceUrl: string)
  : [ResettableTimeout, IAuthenticationProvider] {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.ExecuteSynchronousTasksStageDuration);

  CheckNetworkConnection();

  const timeoutAssumesHang: ResettableTimeout = new ResettableTimeout(() => {
    HandleError(new BootError(BootErrors.MaxBootTimeReached, "Reached max boot time of " + MAX_BOOT_TIME_MILLISECONDS), timeoutAssumesHang, setIsAppLoaded, history);
  }, MAX_BOOT_TIME_MILLISECONDS);

  // Detect environment from Augloop URL once and reuse for both icons and auth provider
  const authEnvironment = getAuthEnvironmentFromAugLoopServiceUrl(augloopServiceUrl);

  // If change gate isUseCustomIconServer is enabled, initialize icons from environment-specific URL.
  // Otherwise, initialize icons from default server.
  isUseCustomIconServer ? initializeIcons(getIconBaseUrlForEnvironment(authEnvironment)) : initializeIcons();

  if (HostAppUtil.GetHostAppName() === HostAppName.ONENOTE) {
    InitializeOneNoteTranscriptionManager(history);
  }

  if (HostAppSettings.IsSettingTrue(HostAppSettingName.IsRecordingEventCaptureEnabled)) {
    RecordingEventManager.Initialize();
  }

  const authProvider: IAuthenticationProvider = CreateAuthenticationProvider(authEnvironment);
  authProvider.Authenticate();
  setAuthProvider(authProvider);

  if (BroadcastService.IsNotifyTranscriptionEventToHostAppEnabled()) {
    InitializeHostAppNotifer();
  }

  LogEvents.Boot.EndDurationMeasure(BootKeys.ExecuteSynchronousTasksStageDuration);
  return [timeoutAssumesHang, authProvider];
}

function GetPaneBuildNumber() {
  const regex = /\/([0-9.]+)\/en-us_web\//;
  const matches = window.location.href.match(regex);

  if (matches == null || matches.length < 2)
    return '';

  // Return the second match, which contains the first capture group.
  return matches[1];
}

function constructHostAppSettingsJSONString(): string {
  let jsonObject: any = {};

  jsonObject[HostAppSettingName.HasStoredTranscription] = HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription);
  jsonObject[HostAppSettingName.IsFreemiumUpsellEnabled] = HostAppSettings.IsSettingTrue(HostAppSettingName.IsFreemiumUpsellEnabled);
  jsonObject[HostAppSettingName.IsLicensedUser] = HostAppSettings.IsSettingTrue(HostAppSettingName.IsLicensedUser);
  jsonObject[HostAppSettingName.EnableFlightAuthVersion] = HostAppSettings.IsSettingTrue(HostAppSettingName.EnableFlightAuthVersion);
  jsonObject[BootKeys.IsUnlimitedFileUploadMinutesAvailableForTenant] = IsUnlimitedFileUploadMinutesAvailableForUser();
  jsonObject[HostAppSettingName.NotifyTranscriptionEventToHostAppEnabled] = HostAppSettings.IsSettingTrue(HostAppSettingName.NotifyTranscriptionEventToHostAppEnabled);
  jsonObject[HostAppSettingName.IsRecordingEventCaptureEnabled] = HostAppSettings.IsSettingTrue(HostAppSettingName.IsRecordingEventCaptureEnabled);
  jsonObject[HostAppSettingName.IsMultiTranscriptionStorageSupported] = HostAppSettings.IsSettingTrue(HostAppSettingName.IsMultiTranscriptionStorageSupported);
  jsonObject[HostAppSettingName.ShouldShowOneDriveIdentityInfoDialog] = HostAppSettings.IsSettingTrue(HostAppSettingName.ShouldShowOneDriveIdentityInfoDialog);
  jsonObject[HostAppSettingName.AddTranscriptionIndicatorToHostEnabled] = HostAppSettings.IsSettingTrue(HostAppSettingName.AddTranscriptionIndicatorToHostEnabled);
  jsonObject[HostAppSettingName.DocumentStorageType] = HostAppSettings.GetSettingAsString(HostAppSettingName.DocumentStorageType);
  jsonObject[HostAppSettingName.IsTranscriptionFetchedFromCustomProperties] = HostAppSettings.IsSettingTrue(HostAppSettingName.IsTranscriptionFetchedFromCustomProperties);
  jsonObject[HostAppSettingName.IsCopilotLicensedUser] = HostAppSettings.IsSettingTrue(HostAppSettingName.IsCopilotLicensedUser);

  // OneNote Specific Setting
  if (HostAppUtil.GetHostAppName() == HostAppName.ONENOTE) {
    jsonObject[BootKeys.IsOneNoteNotebookIdentityEmpty] = IsOneNoteNotebookIdentityEmpty();
  }

  let jsonString = JSON.stringify(jsonObject);
  return jsonString
}

// Do any initialization required at the beginning of boot.
function InitializeBoot(): string {
  Logger.setLoggerInstance(LoggerFactory.createLoggerInstance(LoggerFactory.LoggerType.OTelBasedLogger));

  const transcriptionSessionId: string = HostAppSettings.GetSettingAsString(HostAppSettingName.TranscriptionSessionId);

  LogEvents.Boot.Start([
    { key: BootKeys.TranscriptionSessionId, value: transcriptionSessionId },
    { key: BootKeys.TranscriptionPaneBuildNumber, value: GetPaneBuildNumber() },
    { key: BootKeys.TranscriptAssociated, value: HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription) },
    { key: BootKeys.IsFreemiumUpsellEnabled, value: HostAppSettings.IsSettingTrue(HostAppSettingName.IsFreemiumUpsellEnabled) },
    { key: BootKeys.IsLicensedUser, value: HostAppSettings.IsSettingTrue(HostAppSettingName.IsLicensedUser) },
    { key: BootKeys.IsCopilotLicensedUser, value: HostAppSettings.IsSettingTrue(HostAppSettingName.IsCopilotLicensedUser) },
    { key: BootKeys.SDXPackageVersion, value:HostAppSettings.GetSettingAsString(HostAppSettingName.SDXPackageVersion) },
  ]);
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.DurationSeconds);
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.InitializeBootStageDuration);

  GlobalErrorMonitor.Start(LogEventNames.Boot);
  console.log('Transcription session ID: ' + transcriptionSessionId);

  // This is necessary here because InitializeBackupState async task could call into SetInitialPageFromBackupStateAndStoredTranscription before the later synchronous tasks are executed
  const augloopServiceUrl = StartInitializingAugLoopRuntimeManager();

  LogEvents.Boot.EndDurationMeasure(BootKeys.InitializeBootStageDuration);

  return augloopServiceUrl;
}

// Do any final bookkeeping at the end of boot.
function FinalizeBoot(finishedSuccessfully: boolean, setIsAppLoaded: (isAppLoaded: boolean) => void, timeoutAssumesHang: ResettableTimeout|null) {
  LogEvents.Boot.BeginDurationMeasure(DurationMeasureType.Normal, BootKeys.FinalizeBootStageDuration);

  // Set app as loaded so we show the initial page in the next render.
  setIsAppLoaded(true);

  if (timeoutAssumesHang) {
    timeoutAssumesHang.Cancel();
    timeoutAssumesHang = null;
  }

  LogEvents.Boot.AddData([{ key: BootKeys.DidBootFinishSuccessfully, value: finishedSuccessfully}]);
  LogEvents.Boot.EndDurationMeasure(BootKeys.FinalizeBootStageDuration);

  GlobalErrorMonitor.SetErrorArea(LogEventNames.Warning);
}

async function CheckCopilotLicense(authProvider: IAuthenticationProvider): Promise<void> {
  if(HostAppSettings.IsSettingTrue(HostAppSettingName.IsLicensedUser)){
    return;
  }

  LogEvents.Boot.AddData([{ key: BootKeys.IsCopilotLicensedCheckEnabled, value: enableCopilotLicenseCheck }]);

  const authEnv = getAuthEnvironment();

  // Since GCC, GCCHigh and DOD do not have exp settings,
  // we enable the copilot license check feature with code.
  // If the copilot license check is disabled, we don't need to check the copilot license
  if (!enableCopilotLicenseCheck &&
    authEnv !== AuthEnvironment.GCC &&
    authEnv !== AuthEnvironment.GCCHigh &&
    authEnv !== AuthEnvironment.DOD
  ) {
    return;
  }

  const copilotLicensingTask = new CopilotLicensingTask();

  try {
    const hasLicense = await copilotLicensingTask.checkCopilotLicense(authProvider);
    HostAppSettings.Set(HostAppSettingName.IsCopilotLicensedUser, hasLicense);
    LogEvents.Boot.AddData([{ key: BootKeys.IsCopilotLicensedUser, value: hasLicense }]);
  } catch (error) {
    LogError(LogEventNames.Boot, "failed to check copilot license: " + ErrorStringFromException(error));
  }
}

// Boot is composed of the following sequential stages:
// 1. Initialize boot
// 2. Initialize async tasks
// 3. Execute synchronous tasks
// 4. Wait for async tasks to complete
// 5. Finalize boot
export async function InitializeApp(history: any, setAuthProvider: React.Dispatch<React.SetStateAction<IAuthenticationProvider | null>>, setIsAppLoaded: (isAppLoaded: boolean) => void) {
  let timeoutAssumesHang: ResettableTimeout|null = null;

  try {
    RegisterServiceWorker();
    const augloopServiceUrl = InitializeBoot();

    const [timeoutAssumesHang, authProvider] = ExecuteSynchronousTasks(history, setAuthProvider, setIsAppLoaded, augloopServiceUrl);

    const asyncTasks: Promise<any>[] = InitializeAsyncTasks(history, authProvider);

    await WaitForAsyncTasks(asyncTasks);

    FinalizeBoot(true /*finishedSuccessfully*/, setIsAppLoaded, timeoutAssumesHang);

    BroadcastService.GetInstance().BroadcastEvent(TranscriptionNotifyEvent.TRANSCRIPION_LAUNCHED);
  } catch (error) {
    try {
      if (loadTranscriptionStringsTask == null) {
        throw new BootError(BootErrors.InvalidLoadTranscriptionStringsPromise, "Load transcription strings promise was null when handling error. Error being handled: " + ErrorStringFromException(error));
      }

      // Wait for strings to load before showing the error message.
      await loadTranscriptionStringsTask;
      HandleError(error, timeoutAssumesHang, setIsAppLoaded, history);
    } catch (loadStringsError) {
      // If we failed loading strings, just go through normal error handling which will handle LoadTranscriptionStringsError and BootErrors.InvalidLoadTranscriptionStringsPromise.
      HandleError(loadStringsError, timeoutAssumesHang, setIsAppLoaded, history);
    }
  } finally {
    const hostAppSettingsJSON = constructHostAppSettingsJSONString();

    LogEvents.Boot.End([
      { key: BootKeys.InitialTranscriptionRoutePathName, value: history.location.pathname},
      { key: BootKeys.UnlimitedMinsActive, value: HostAppSettings.IsSettingTrue(
          HostAppSettingName.UnlimitedMins,
      )},
      { key: BootKeys.HostAppSettingsJSON, value: hostAppSettingsJSON }
    ]);
  }
}

function HandleError(e: any, timeoutAssumesHang: ResettableTimeout|null, setIsAppLoaded: (isAppLoaded: boolean) => void, history: any) {
  let failureArea = LogEventNames.Boot;
  if (ShouldLogErrorAsWarning(e)) {
    failureArea = LogEventNames.Warning;
  }

  LogError(failureArea, "Opening pane failed with error: " + ErrorStringFromException(e));

  if (!(e instanceof NetworkError && e.name == NetworkErrors.NoInternetConnection) && !IsConnectedToNetwork()) {
    LogError(LogEventNames.Boot, "Opening pane failed with error: " + TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason);
  }

  const transcriptionErrorEvent : ITranscriptionErrorEvent = GetFailureReasonForInitializeAppError(e);

  // Show Failure page
  SetFailureStateAndRouteToFailure(failureArea, transcriptionErrorEvent, FailureContinuation.None /*failureContinuation*/, history);

  FinalizeBoot(false /*finishedSuccessfully*/, setIsAppLoaded, timeoutAssumesHang);
}